/**
 * Integrations registry — the HONEST map of every connection Argo OS can
 * make, what each one UNLOCKS, whether it is actually wired right now, and
 * exactly how to connect it.
 *
 * This module is intentionally PURE: it derives nothing from the network and
 * reads no globals. `buildIntegrations(env)` takes a snapshot of the REAL,
 * client-visible configuration — the `isConfigured()` results of each seam
 * plus the handful of `import.meta.env` flags the browser can see — and maps
 * it to a list of cards. The caller (AdminView) is the only place that reads
 * `import.meta.env` / calls the seams; that keeps status derivation testable
 * with plain object injection and guarantees every status reflects real
 * config, never aspiration.
 *
 * Status vocabulary (three states, no fourth "aspirational" state):
 *   - 'connected'      → wired to a real backend the client can prove is set.
 *   - 'partially'      → some capability is live, but the full surface isn't
 *                        (e.g. transactional email sends, but no inbound yet).
 *   - 'not_connected'  → the seam exists in code but no endpoint is configured;
 *                        or it isn't built at all. `detail` says which.
 */

export type IntegrationStatus = 'connected' | 'not_connected' | 'partially';

export interface Integration {
  /** Stable id for keys / tests. */
  id: string;
  /** Display name. */
  name: string;
  /** One plain-English line: what turning this on UNLOCKS. */
  purpose: string;
  /** Real, derived status. */
  status: IntegrationStatus;
  /** WHY it has that status — phrased so an operator can trust it. */
  detail: string;
  /** Exactly what to do to connect (which env var / key / who to ask). */
  connectHint: string;
  /**
   * True when connecting is an env-side / backend change the operator can't
   * do from this screen — so the card must NOT show a fake "Connect" button.
   */
  envManaged: boolean;
}

/**
 * A snapshot of the REAL, client-visible configuration. The caller fills
 * each field from the seams' own `isConfigured()` and from `import.meta.env`
 * — this module never reads either, so tests drive it with a plain object.
 */
export interface IntegrationEnv {
  /** argoWorker.isConfigured() — VITE_AGENT_API_URL is set. */
  agentWorker: boolean;
  /** contentEngine.isConfigured() — VITE_CONTENT_ENGINE_URL is set. */
  contentEngine: boolean;
  /** enrich.isConfigured() — VITE_ENRICH_API_URL is set. */
  enrich: boolean;
  /** meetNotes/emailLog.isConfigured() — VITE_GSUITE_API_URL is set (Google). */
  google: boolean;
  /** slackNotify.isConfigured() — VITE_SLACK_API_URL is set. */
  slack: boolean;
  /**
   * Firestore is wired (VITE_USE_FIRESTORE + apiKey). Email delivery rides
   * the `outbox` collection → Cloud Function → Brevo, so without Firestore
   * the transactional send path can't even queue.
   */
  firestore: boolean;
  /**
   * A GitHub repo token is wired into the backend so approved patches open
   * as real pull requests. The token/owner/repo live server-side (never in
   * the browser — see src/lib/integrations/github.ts), so the client can
   * only ever be told whether the backend has it, never hold it itself.
   */
  githubRepo: boolean;
}

/**
 * Build the honest integration list from a config snapshot. Pure and total:
 * every branch is decided solely by `env`, so the same snapshot always
 * yields the same cards.
 */
export function buildIntegrations(env: IntegrationEnv): Integration[] {
  return [
    claudeWorker(env),
    hyperCoderWorker(env),
    github(env),
    scoringEngine(env),
    emailDelivery(env),
    googleWorkspace(env),
    slack(env),
    zernio(env),
    stripe(),
  ];
}

function claudeWorker(env: IntegrationEnv): Integration {
  const on = env.agentWorker;
  return {
    id: 'claude-worker',
    name: 'LLM agent worker',
    purpose:
      'Real AI answers, drafts, and agent runs — Ask Argo, digests and content compose from a live LLM (Anthropic, OpenAI, or Google Gemini) instead of the built-in simulation.',
    status: on ? 'connected' : 'not_connected',
    detail: on
      ? 'VITE_AGENT_API_URL is set — live LLM answers are on. Every agent call routes to the hosted worker (provider-agnostic — the model is chosen in Admin → Settings); nothing else in the app talks to the network for agent work.'
      : 'VITE_AGENT_API_URL is unset — the app runs the deterministic in-app simulation. Everything still works; answers are canned, not a live LLM.',
    connectHint: on
      ? 'Connected. To point at a different worker, change VITE_AGENT_API_URL in the deploy env.'
      : 'Set VITE_AGENT_API_URL (the hosted agent-worker endpoint) in the deploy env. This is a server-side flag — ask your admin / whoever owns the deploy.',
    envManaged: true,
  };
}

function hyperCoderWorker(env: IntegrationEnv): Integration {
  // The HyperCoder view reads worker liveness from argoWorker.workerInfo()
  // (.configured), which is the SAME VITE_AGENT_API_URL flag as the Claude
  // worker — dispatches route through the one agent-worker seam. When unset
  // the view honestly shows "Coding worker: SIMULATION / No agent worker
  // configured" and runs the deterministic in-app fallback.
  const on = env.agentWorker;
  return {
    id: 'hypercoder-worker',
    name: 'HyperCoder coding worker',
    purpose:
      'Agents write reviewable, reversible code patches from tickets — a ticket becomes a scoped contract, the worker returns a file patch, and every approved change ships with its exact inverse.',
    status: on ? 'connected' : 'not_connected',
    detail: on
      ? 'VITE_AGENT_API_URL is set — HyperCoder dispatches run on the live Argo agent worker (the same seam that powers Ask Argo). Scope gates enforce the blast radius before any diff exists.'
      : 'VITE_AGENT_API_URL is unset — the HyperCoder view honestly shows the coding worker as SIMULATION ("No agent worker configured"). The full govern → review → approve → revert loop still works against the deterministic in-app fallback; patches are canned, not written by a live agent.',
    connectHint: on
      ? 'Connected. HyperCoder shares the agent-worker endpoint — change VITE_AGENT_API_URL in the deploy env to point at a different coding worker.'
      : 'Set VITE_AGENT_API_URL (the hosted agent-worker endpoint HyperCoder dispatches to) in the deploy env. Server-side flag — ask your admin / whoever owns the deploy.',
    envManaged: true,
  };
}

function github(env: IntegrationEnv): Integration {
  // The GitHub PR client (src/lib/integrations/github.ts) is ready but takes
  // an explicit { token, owner, repo } — it never reads env for the token.
  // So the client can only ever be TOLD whether the backend holds a repo
  // token; the token itself lives server-side. Without it, dispatch proposes
  // and reviews patches in-app but the HyperCoder view shows "GitHub loop:
  // NOT CONNECTED" because no PR can open.
  const on = env.githubRepo;
  return {
    id: 'github',
    name: 'GitHub',
    purpose:
      'Approved patches open as pull requests on your repo — the HyperCoder dispatch loop lands reviewable draft PRs instead of stopping at an in-app diff.',
    status: on ? 'connected' : 'not_connected',
    detail: on
      ? 'A GitHub repo token is wired into the backend — approved HyperCoder patches open as real draft pull requests via the PR client (src/lib/integrations/github.ts). The token, owner and repo live server-side; nothing touches the browser.'
      : 'No GitHub repo token is wired into the backend, so the HyperCoder view honestly shows the GitHub loop as NOT CONNECTED. Dispatch still proposes and reviews patches in-app — but no pull request can open until the repo is connected.',
    connectHint: on
      ? 'Connected. The GitHub repo token/owner/repo live in the backend env (never in the browser). Change the repo token there to point at a different repository.'
      : 'Wire a GitHub repo token (token + owner + repo) into the backend env — the PR client (src/lib/integrations/github.ts) is ready and takes it explicitly, never from the browser. Server-side change — ask your admin / whoever owns the deploy.',
    envManaged: true,
  };
}

function scoringEngine(env: IntegrationEnv): Integration {
  const on = env.contentEngine;
  return {
    id: 'argoiq-scoring',
    name: 'ArgoIQ scoring engine',
    purpose:
      'Real AQI content scoring & auto-fix — every draft is graded on the six-dimension rubric by the deployed engine instead of the local estimate.',
    status: on ? 'connected' : 'not_connected',
    detail: on
      ? 'VITE_CONTENT_ENGINE_URL is set — scores come from the real ArgoIQ engine (via our proxy). The engine passcode lives only in the proxy env, never in the browser.'
      : 'VITE_CONTENT_ENGINE_URL is unset — scores are a deterministic local estimate, flagged "Simulated" wherever they appear. Same rubric, honest label.',
    connectHint: on
      ? 'Connected. The engine passcode is held server-side in the proxy function, not here.'
      : 'Set VITE_CONTENT_ENGINE_URL (points at the content-engine proxy). Server-side flag — ask your admin.',
    envManaged: true,
  };
}

function emailDelivery(env: IntegrationEnv): Integration {
  // Transactional sending is built-in via outbox → Cloud Function → Brevo,
  // but that path needs Firestore to queue the outbox doc. Inbound/replies and
  // custom sending domains are NOT built yet — so this is honestly 'partially'.
  const canQueue = env.firestore;
  return {
    id: 'email-delivery',
    name: 'Email delivery (Brevo)',
    purpose:
      'Send transactional & governed customer email — weekly updates, sequence sends and one-off mail leave the building through Brevo.',
    status: canQueue ? 'partially' : 'not_connected',
    detail: canQueue
      ? 'Transactional sending is built in: the app writes to the Firestore `outbox` collection and a Cloud Function delivers it via Brevo — no public send endpoint, so it works under strict org sharing policies. Not yet built: inbound/replies capture and custom sending domains — those are coming.'
      : 'Sending rides the Firestore `outbox` collection, but Firestore isn\'t wired here — nothing can be queued yet. Inbound/replies and custom sending domains are coming regardless.',
    connectHint: canQueue
      ? 'Outbound is live. The Brevo API key lives in the Cloud Function env (functions/src/sendEmail.ts), never in the browser. Inbound & custom domains: on the roadmap.'
      : 'Enable Firestore first (VITE_USE_FIRESTORE + VITE_FIREBASE_* keys), then the Brevo key is set in the Cloud Function env by your admin.',
    envManaged: true,
  };
}

function googleWorkspace(env: IntegrationEnv): Integration {
  const on = env.google;
  return {
    id: 'google-workspace',
    name: 'Google Workspace (Gmail & Meet)',
    purpose:
      'Auto-log the timeline — pull Gmail threads and Google Meet notes onto contact & account records so the activity trail captures touches on its own.',
    status: on ? 'connected' : 'not_connected',
    detail: on
      ? 'VITE_GSUITE_API_URL is set — Gmail and Meet sync route to the hosted Workspace worker, which owns the OAuth and the Google API calls. No Google secrets touch the browser.'
      : 'The seam is in the code (Sync Gmail / Sync Meet notes buttons work today against a deterministic simulation), but no Workspace worker is configured, so syncs are simulated — not your real inbox/calendar.',
    connectHint: on
      ? 'Connected. OAuth + Gmail/Calendar access live behind VITE_GSUITE_API_URL.'
      : 'Set VITE_GSUITE_API_URL (the hosted Workspace worker that holds the Google OAuth). Server-side — ask your admin. No OAuth flow lives in this app.',
    envManaged: true,
  };
}

function slack(env: IntegrationEnv): Integration {
  const on = env.slack;
  return {
    id: 'slack',
    name: 'Slack',
    purpose:
      'Push notifications to Slack — agent alerts and governed updates post to your channels instead of only living in-app.',
    status: on ? 'connected' : 'not_connected',
    detail: on
      ? 'VITE_SLACK_API_URL is set — messages post to the real Slack workspace via the hosted worker, which holds the Slack OAuth token.'
      : 'The seam is in the code, but no Slack worker is configured, so posts are simulated (logged, not delivered). No fake channel is invented.',
    connectHint: on
      ? 'Connected. The Slack token lives behind VITE_SLACK_API_URL, never in the browser.'
      : 'Set VITE_SLACK_API_URL (the hosted Slack worker that holds the OAuth token). Server-side — ask your admin.',
    envManaged: true,
  };
}

function zernio(env: IntegrationEnv): Integration {
  // Zernio social publishing rides the same scoring/content engine surface;
  // its live-ness is gated by the same VITE_CONTENT_ENGINE_URL.
  const on = env.contentEngine;
  return {
    id: 'zernio-social',
    name: 'Zernio social publishing',
    purpose:
      'Publish scored content to social — approved, on-brand posts flow out through Zernio via the scoring engine surface.',
    status: on ? 'partially' : 'not_connected',
    detail: on
      ? 'Rides the ArgoIQ scoring engine (VITE_CONTENT_ENGINE_URL is set), so scored drafts can flow toward publishing. Direct one-click social publishing is still being wired — today the engine scores; the publish leg is partial.'
      : 'Depends on the ArgoIQ scoring engine, which isn\'t configured — VITE_CONTENT_ENGINE_URL is unset. Content scores locally (Simulated) and doesn\'t publish out.',
    connectHint: on
      ? 'Scoring is live. Full publishing is on the roadmap — no invented "Publish" button until the leg is real.'
      : 'Set VITE_CONTENT_ENGINE_URL (the scoring engine Zernio rides on). Server-side — ask your admin.',
    envManaged: true,
  };
}

function stripe(): Integration {
  // Honest: not built. No env flag can make this true today.
  return {
    id: 'stripe-billing',
    name: 'Stripe billing',
    purpose:
      'Charge customers & manage subscriptions — invoices, plans and payment status inside Argo OS.',
    status: 'not_connected',
    detail:
      'Not built yet. There is no Stripe seam in the app today — no charges, plans, or webhooks. We\'re calling that out plainly rather than showing a dead "Connect" button.',
    connectHint:
      'Nothing to configure yet — billing isn\'t implemented. Tell your admin if this is a priority so it can be scheduled.',
    envManaged: false,
  };
}
